import { ToastProgrammatic as Toast } from 'buefy';
import isEqual from 'lodash/isEqual';
import { mapActions, mapMutations, mapState } from 'vuex';

import { cloneDeep, get } from 'lodash';
import {
  SET_DEPARTMENT_LIST,
  UPDATE_DRAFT_ORDER,
} from '@/store/orders/constants';
import { GET_DEPARTMENTS } from '@/store/setting/constants';
import { ALLOW_EDIT_TYPES, NO_DEPARTMENT_ID, NO_DEPARTMENT_NAME } from '@/utils/constants';

export default {
  name: 'MemberTable',
  props: {
    adjustable: {
      type: Boolean,
      default: true,
    },
    allowEditType: {
      default: ALLOW_EDIT_TYPES.ALL,
      type: Number,
      validator: (val) => Object.values(ALLOW_EDIT_TYPES).includes(val),
    },
    columns: {
      type: Array,
      default: () => ['selected', 'name'],
    },
    currentPage: {
      default: 0,
      type: Number,
    },
    defaultSelectAll: {
      default: false,
      type: Boolean,
    },
    emptyText: {
      default: '沒有人員',
      type: String,
    },
    members: {
      required: true,
      type: Array,
    },
    originSelectedMembers: {
      default: () => ({}),
      type: Object,
    },
    perPage: {
      default: 0,
      type: Number,
    },
    search: {
      type: String,
      default: '',
    },
    selectable: {
      type: Boolean,
      default: true,
    },
    selectedDepartmentIds: {
      default: () => ([]),
      type: Array,
    },
    selectedMemberIds: {
      default: () => ([]),
      type: Array,
    },
    selectedMembers: {
      default: () => ({}),
      type: Object,
    },
    totalPage: {
      default: 0,
      type: Number,
    },
    useMemberDerivedDepartments: {
      type: Boolean,
      default: false,
    },
    warnEmpty: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      adjustableColumns: [
        {
          column: 'id',
          label: '人員編號',
          visible: true,
        },
        {
          column: 'department',
          label: '部門',
          visible: true,
        },
        {
          column: 'extension',
          label: '分機/電話',
          visible: false,
        },
        {
          column: 'mobile',
          label: '手機號碼',
          visible: false,
        },
        {
          column: 'note',
          label: '備註',
          visible: false,
        },
      ],
      allMembers: [],
      isAllDepartmentSelected: false,
      selectedDepartments: [],
    };
  },
  computed: {
    ...mapState('orders', ['departments']),
    allowEditAll() {
      return this.allowEditType === ALLOW_EDIT_TYPES.ALL;
    },
    columnVisibility() {
      const mapping = {};

      this.adjustableColumns.forEach((c) => {
        const showColumn = this.columns.indexOf(c.column) > -1;

        mapping[c.column] = showColumn || c.visible;
      });
      return mapping;
    },
    isAllMemberSelected() {
      return this.allMembers
        .filter((member) => this.checkMemberSelected(this.selectedMembers, member))
        .length === this.allMembers.length;
    },
    memberDerivedDepartmentCounts() {
      const result = {};
      this.allMembers.forEach((member) => {
        const id = member.department_id ?? NO_DEPARTMENT_ID;
        result[id] = (result[id] || 0) + 1;
      });
      return result;
    },
    displayDepartments() {
      if (!this.useMemberDerivedDepartments) {
        return this.departments;
      }
      const counts = this.memberDerivedDepartmentCounts;
      const ids = Object.keys(counts).map(Number)
        .filter((id) => id !== NO_DEPARTMENT_ID)
        .sort((a, b) => {
          const nameA = this.getDepartmentName(a) || '';
          const nameB = this.getDepartmentName(b) || '';
          return nameA.localeCompare(nameB, 'zh-TW');
        });
      const result = ids.map((id) => ({
        id,
        name: this.getDepartmentName(id),
        member_count: counts[id],
      }));
      if (counts[NO_DEPARTMENT_ID] > 0) {
        result.push({
          id: NO_DEPARTMENT_ID,
          name: NO_DEPARTMENT_NAME,
          member_count: counts[NO_DEPARTMENT_ID],
        });
      }
      return result;
    },
    departmentInfo() {
      if (this.useMemberDerivedDepartments) {
        return Object.keys(this.memberDerivedDepartmentCounts).map(Number);
      }
      return this.departments?.map(({ id }) => id) || [];
    },
    allMemberCount() {
      if (this.useMemberDerivedDepartments) {
        return this.allMembers.length;
      }
      return this.departments?.reduce((pre, cur) => pre + (cur?.member_count || 0), 0);
    },
    notAllowToEditMembers() {
      return this.allowEditType === ALLOW_EDIT_TYPES.NO_EDIT_MEMBERS;
    },
    noMorePage() {
      return this.totalPage > 0 && this.currentPage >= this.totalPage;
    },
    filteredMembers() {
      return this.allMembers.filter(
        (member) => this.selectedDepartments.includes(
          member.department_id ?? NO_DEPARTMENT_ID,
        ),
      );
    },
  },
  async created() {
    await this.loadDepartmentInfo();
  },
  watch: {
    members(newVal) {
      this.allMembers = newVal;
      if (this.useMemberDerivedDepartments && this.isAllDepartmentSelected) {
        this.selectAllDepartments(true);
      }
    },
    noMorePage(newVal) {
      if (newVal) {
        this.setSelectedDepartments(this.selectedDepartmentIds);
      }
    },
    selectedDepartments(newVal, oldVal) {
      if (!isEqual([...newVal].sort(), [...oldVal].sort())) {
        this.$emit('selected-departments', newVal);
      }
    },
    selectedDepartmentIds(newVal) {
      this.selectedDepartments = newVal || [];
      this.departmentChanged();
    },
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  mounted() {
    this.allMembers = this.members;
    this.selectedDepartments = this.selectedDepartmentIds;
  },
  methods: {
    ...mapActions('setting', {
      getDepartments: GET_DEPARTMENTS,
    }),
    ...mapMutations('orders', {
      setDepartmentList: SET_DEPARTMENT_LIST,
      updateDraftOrder: UPDATE_DRAFT_ORDER,
    }),
    columnThAttrs() {
      return {
        class: 'text-xs',
      };
    },
    async loadDepartmentInfo() {
      try {
        this.loading = true;
        const departments = await this.getDepartments();
        this.setDepartmentList(departments);
        this.selectAllDepartments(this.defaultSelectAll);
      } catch {
        Toast.open({
          message: '載入部門資訊失敗，請重試一次',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    getDepartmentName(departmentId) {
      const department = this.departments.find((d) => String(d.id) === String(departmentId));
      return department?.name;
    },
    getDepartmentCount(departmentId) {
      if (this.useMemberDerivedDepartments) {
        return this.memberDerivedDepartmentCounts[departmentId] || 0;
      }
      const department = this.departments.find((d) => String(d.id) === String(departmentId));
      return department?.member_count || 0;
    },
    getRowClasses(row) {
      const cannotSelect = this.notAllowToEditMembers
        || this.checkMemberSelected(this.originSelectedMembers, row)
        || !this.selectable;
      return cannotSelect ? 'cursor-default' : 'cursor-pointer';
    },
    goToCreatePage() {
      this.$router.push('/member/create');
    },
    selectAllDepartments(selectAll) {
      this.isAllDepartmentSelected = selectAll;
      this.selectedDepartments = selectAll ? this.departmentInfo : [];
    },
    selectAllMembers() {
      const updateData = cloneDeep(this.selectedMembers);
      this.selectedDepartments.forEach((departmentId) => {
        delete updateData.includes?.[departmentId];
        if (!updateData.excludes) {
          updateData.excludes = {};
        }
        updateData.excludes[departmentId] = [];
      });
      this.$emit('update-selected-members', updateData);
      this.updateDraftOrder({
        selectedMembers: updateData,
      });
    },
    setSelectedDepartments(ids = []) {
      const departmentIds = ids.map((id) => id);
      if (!departmentIds.length || isEqual(
        departmentIds.sort(),
        this.departmentInfo.sort(),
      )) {
        this.selectAllDepartments(true);
        return;
      }
      this.isAllDepartmentSelected = false;
    },
    sortByField(field) {
      return (a, b, isAsc) => {
        const dir = isAsc ? 1 : -1;
        const aVal = this.getFieldValue(a, field);
        const bVal = this.getFieldValue(b, field);
        if (aVal === bVal) return 0;
        return aVal > bVal ? dir : -dir;
      };
    },
    getFieldValue(row, field) {
      const map = {
        name: 'member_detail.name',
        serial: 'member_detail.serial',
        department_id: 'department_name',
        ext: 'member_detail.ext',
        phone: 'member_detail.phone',
        note: 'member_detail.note',
      };
      return get(row, map[field] || field);
    },
    departmentChanged() {
      this.isAllDepartmentSelected = this.departmentInfo?.every(
        (id) => this.selectedDepartments?.includes(id),
      );
    },
    checkMemberSelected(selectedMembers, target) {
      const { department_id: departmentId, id: targetMemberId } = target;
      const { excludes = {}, includes = {} } = selectedMembers;
      if (excludes[departmentId] && !excludes[departmentId]?.length) {
        return true;
      }
      if (includes[departmentId]?.includes(targetMemberId)) {
        return true;
      }
      if (excludes[departmentId]) {
        return !excludes[departmentId]?.includes(targetMemberId);
      }
      return false;
    },
    findTargetMemberId(target) {
      return this.checkMemberSelected(this.selectedMembers, target);
    },
    toggleMember(item) {
      if (
        !this.selectable
        || this.notAllowToEditMembers
        || (!this.allowEditAll && this.checkMemberSelected(this.originSelectedMembers, item))
      ) {
        return;
      }
      this.$emit('set-selected-member', item);
    },
  },
};
